import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { toApiError, type ApiError } from '../../api/client';
import { loadOwnerDashboard } from '../../api/endpoints';
import type { OwnerDashboard } from '../../api/types';
import { QuickAction, StatCard, WelcomeHeader } from '../../components/Common';
import { IconAlert, IconCard, IconClock, IconList, IconMail, IconPlus, IconUsers } from '../../components/Icons';
import { ErrorState, TableSkeleton } from '../../components/States';
import { date, money, relativeTime } from '../../utils/format';

export function OwnerDashboardPage() {
  const { isOwner } = useAuth();
  return isOwner ? <OwnerDashboardContent /> : <StaffDashboard />;
}

function StaffDashboard() {
  const { user, canManageHomeRentals } = useAuth();
  const organizationName = user?.businesses[0]?.businessName;
  const assignedBranches = user?.businesses.flatMap((business) =>
    business.assignedBranches.map((branch) => ({
      ...branch,
      businessName: business.businessName,
    })),
  ) ?? [];

  return (
    <>
      <WelcomeHeader name={organizationName ?? user?.fullName} />
      <div className="sf-card sf-card-pad mb-3">
        <h2 className="sf-section-title">Staff overview</h2>
        <p className="mb-1"><strong>Organisation:</strong> {user?.businesses[0]?.businessName ?? 'Not available'}</p>
        <p className="mb-0"><strong>Assigned branches:</strong> {assignedBranches.length}</p>
      </div>

      <h2 className="sf-section-title">Your assigned branches</h2>
      {assignedBranches.length === 0 ? (
        <div className="sf-card sf-card-pad sf-muted">
          No active branch assignments are available. Contact the business owner.
        </div>
      ) : (
        <div className="d-flex flex-column gap-2 mb-4">
          {assignedBranches.map((branch) => (
            <Link
              className="sf-card sf-card-pad text-decoration-none"
              key={`${branch.propertyId}-${branch.businessName}`}
              to={`/owner/listings/${branch.propertyId}`}
            >
              <strong>{branch.name}</strong>
              <span className="sf-muted d-block small">{branch.businessName}</span>
            </Link>
          ))}
        </div>
      )}

      <div className="sf-quickgrid">
        <QuickAction
          to="/owner/branches"
          icon={<IconUsers />}
          label="My assigned branches"
          description="Open a branch assigned to your staff account."
        />
        <QuickAction
          to="/owner/enquiries"
          icon={<IconMail />}
          label="View enquiries"
          description="Review enquiries available to your account."
        />
        {canManageHomeRentals && (
          <QuickAction
            to="/owner/listings/new?category=HomeRental"
            icon={<IconPlus />}
            label="Add a HomeRental listing"
            description="Your account has permission to manage HomeRental listings."
          />
        )}
      </div>
    </>
  );
}

function OwnerDashboardContent() {
  const { user } = useAuth();
  const organizationName = user?.businesses[0]?.businessName;
  const [data, setData] = useState<OwnerDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      setData(await loadOwnerDashboard());
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <TableSkeleton rows={6} columns={4} />;
  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!data) return null;

  const needsAttention =
    data.pendingPaymentTotalCount + data.expiredCount + data.staleAvailabilityCount + data.expiringSoonCount;

  return (
    <div className="sf-dashboard">
      <WelcomeHeader name={organizationName ?? user?.fullName} />

      <div className="sf-quickgrid sf-dashboard__actions">
        <QuickAction
          to="/owner/listings/new"
          icon={<IconPlus />}
          label="Add a listing"
          description="Create a property and take it through to publication."
        />
        <QuickAction
          to="/owner/branches"
          icon={<IconUsers />}
          label="Manage branches"
          description="Branch details, staff accounts and subscriptions."
        />
        <QuickAction
          to="/owner/enquiries"
          icon={<IconMail />}
          label="View enquiries"
          description="Everyone who has asked about one of your listings."
        />
      </div>

      {needsAttention > 0 && (
        <section className="sf-attention" aria-labelledby="needs-attention">
          <h2 className="sf-attention__title" id="needs-attention">
            <IconAlert /> Needs your attention
          </h2>

          <div className="sf-attention__rows">
            {data.pendingPaymentTotalCount > 0 && (
              <Link className="sf-attention__row" to="/owner/listings?status=PendingPayment">
                <IconCard className="sf-attention__icon" />
                <span>
                  <strong>{data.pendingPaymentTotalCount}</strong> listing
                  {data.pendingPaymentTotalCount === 1 ? '' : 's'} waiting for payment
                </span>
              </Link>
            )}
            {data.expiringSoonCount > 0 && (
              <Link className="sf-attention__row" to="/owner/billing">
                <IconClock className="sf-attention__icon" />
                <span>
                  <strong>{data.expiringSoonCount}</strong> subscription
                  {data.expiringSoonCount === 1 ? '' : 's'} expiring soon
                </span>
              </Link>
            )}
            {data.expiredCount > 0 && (
              <Link className="sf-attention__row" to="/owner/listings?status=Expired">
                <IconAlert className="sf-attention__icon" />
                <span>
                  <strong>{data.expiredCount}</strong> expired listing
                  {data.expiredCount === 1 ? '' : 's'} to renew
                </span>
              </Link>
            )}
            {data.staleAvailabilityCount > 0 && (
              <Link className="sf-attention__row" to="/owner/listings">
                <IconList className="sf-attention__icon" />
                <span>
                  <strong>{data.staleAvailabilityCount}</strong> listing
                  {data.staleAvailabilityCount === 1 ? '' : 's'} showing
                  &ldquo;availability unconfirmed&rdquo; to customers
                </span>
              </Link>
            )}
          </div>
        </section>
      )}

      <div className="row g-3 mb-3 sf-dashboard__stats">
        <div className="col-6 col-lg-3">
          <StatCard value={data.businessCount} label="Businesses" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard value={data.publishedCount} label="Published listings" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard value={data.newEnquiryCount} label="New enquiries" alert={data.newEnquiryCount > 0} />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard value={data.viewCountLast30Days} label="Total listing views" />
        </div>
      </div>

      {/* Category blocks appear only for the categories this owner actually operates in. */}
      <div className="row g-3 mb-3">
        {data.pgBranchCount !== undefined && (
          <div className="col-12 col-lg-4">
            <div className="sf-card sf-card-pad h-100">
              <h2 className="sf-section-title">PG &amp; hostels</h2>
              <p className="mb-1">
                <strong>{data.pgBranchCount}</strong> branch{data.pgBranchCount === 1 ? '' : 'es'}
              </p>
              <p className="mb-0">
                <strong>{data.availableBeds ?? 0}</strong> beds available
                <span className="sf-muted small d-block">
                  Counted from branches whose availability is confirmed.
                </span>
              </p>
            </div>
          </div>
        )}

        {data.homeListingCount !== undefined && (
          <div className="col-12 col-lg-4">
            <div className="sf-card sf-card-pad h-100">
              <h2 className="sf-section-title">Homes for rent</h2>
              <p className="mb-0">
                <strong>{data.homeListingCount}</strong> listing{data.homeListingCount === 1 ? '' : 's'}
                <span className="sf-muted small d-block">Each listing is paid for separately.</span>
              </p>
            </div>
          </div>
        )}

        {data.hotelBranchCount !== undefined && (
          <div className="col-12 col-lg-4">
            <div className="sf-card sf-card-pad h-100">
              <h2 className="sf-section-title">Hotels</h2>
              <p className="mb-1">
                <strong>{data.hotelBranchCount}</strong> branch{data.hotelBranchCount === 1 ? '' : 'es'}
              </p>
              <p className="mb-0">
                <strong>{data.hotelRoomCount ?? 0}</strong> room types configured
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="row g-3 mb-3 sf-dashboard__overview">
        <div className="col-12 col-xl-7">
          <div className="sf-card sf-card-pad h-100 sf-dashboard__listing-status">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="sf-section-title mb-0">Listing status</h2>
              <Link to="/owner/listings" className="small">View listings</Link>
            </div>
            <div className="row g-2">
              {[
                ['Draft', data.draftCount],
                ['Payment due', data.pendingPaymentCount],
                ['In review', data.pendingReviewCount],
                ['Published', data.publishedCount],
                ['Expired', data.expiredCount],
                ['Suspended', data.suspendedCount],
              ].map(([label, value]) => (
                <div className="col-6 col-xl-4" key={label as string}>
                  <StatCard value={value as number} label={label as string} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-5">
          <div className="sf-card sf-card-pad h-100 sf-dashboard__subscription">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="sf-section-title mb-0">Subscriptions</h2>
              <span className={`sf-pill ${data.activeSubscriptionCount > 0 ? 'sf-pill--ok' : 'sf-pill--muted'}`}>
                {data.activeSubscriptionCount > 0 ? 'Active' : 'No active plans'}
              </span>
            </div>
            <div className="sf-dashboard__subscription-count">{data.activeSubscriptionCount}</div>
            <p className="sf-muted mb-3">
              Active branch subscription{data.activeSubscriptionCount === 1 ? '' : 's'}
              {data.expiringSoonCount > 0 && ` · ${data.expiringSoonCount} expiring soon`}
            </p>
            <Link to="/owner/billing" className="btn btn-primary">
              Manage subscriptions <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="row g-3 sf-dashboard__activity">
        <div className="col-12 col-lg-7">
          <div className="sf-card sf-card-pad h-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 className="sf-section-title mb-0">Recent enquiries</h2>
              <Link to="/owner/enquiries" className="small">
                View all
              </Link>
            </div>

            {data.recentEnquiries.length === 0 ? (
              <p className="sf-muted mb-0">No enquiries yet.</p>
            ) : (
              <ul className="list-unstyled mb-0">
                {data.recentEnquiries.map((enquiry) => (
                  <li className="border-bottom py-2" key={enquiry.id}>
                    <div className="d-flex justify-content-between gap-2">
                      <div>
                        <strong>{enquiry.name}</strong>
                        <span className="sf-muted"> · {enquiry.phone}</span>
                        <div className="small sf-muted">{enquiry.propertyTitle}</div>
                      </div>
                      <span className="small sf-muted text-nowrap">{relativeTime(enquiry.createdAtUtc)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="col-12 col-lg-5">
          <div className="sf-card sf-card-pad h-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 className="sf-section-title mb-0">Renewals due</h2>
              <Link to="/owner/billing" className="small">
                Billing
              </Link>
            </div>

            {data.upcomingRenewals.length === 0 ? (
              <p className="sf-muted mb-0">Nothing due in the next two weeks.</p>
            ) : (
              <ul className="list-unstyled mb-0">
                {data.upcomingRenewals.map((subscription) => (
                  <li className="border-bottom py-2" key={subscription.id}>
                    <div className="d-flex justify-content-between gap-2">
                      <div>
                        <strong>{subscription.planName}</strong>
                        <div className="small sf-muted">
                          Expires {date(subscription.expiryUtc)} · {money(subscription.totalAmount)}
                        </div>
                      </div>
                      <span
                        className={`sf-pill ${
                          (subscription.daysRemaining ?? 99) <= 3 ? 'sf-pill--warn' : 'sf-pill--info'
                        }`}
                      >
                        {subscription.daysRemaining} day{subscription.daysRemaining === 1 ? '' : 's'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
